import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import moment from 'moment';
import { validate as isValidUUID } from 'uuid';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { Modal } from '@fortress-technology-solutions/fortress-component-library/Molecules_Fortress';
import {
  PencilIcon,
  PlusIcon,
  AlertIcon,
} from '@fortress-technology-solutions/fortress-component-library/Icons';
import {
  Grid,
  Checkbox,
  Banner,
} from '@fortress-technology-solutions/fortress-component-library/Molecules';
import {
  SubmitButton,
  Button,
  InputLabel,
  Typography,
  Tooltip,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { formatDateDB } from '@fortress-technology-solutions/fortress-component-library/utils';

import RentStep from './RentStep';
import { removeRentStep } from './removeRentStep';
import messages from '../messages';
import { validateLeaseRent as validate } from '../validate';
import { useRentSteps } from '../../../../hooks/data-modifying/useRentStepsMutator';
import { parseAffordableValues } from '../../../../utils/lease-helpers';

const LeaseRentAmountModal = ({
  intl,
  open,
  closeModal,
  leaseRentPercentage,
  unit,
  receivingAssistance,
  affordableValues,
  applicableRents,
  lease,
  property,
  hud236BasicRent,
  hud236MarketRent,
  renewalLeaseStartDate,
  renewalIsRenewalComplete,
  affordableFloorplanPricing,
  utilityAllowanceId,
  refreshLDT,
  receivingAssistanceUpdate,
  isHUDorRD,
}) => {
  const flags = useFlags();
  const {
    rentSteps,
    setRentSteps,
    resetRentSteps,
    saveRentSteps,
    receivingAsstState,
    setReceivingAsstState,
    rentStepsSaving,
    setRentStepsToDelete,
    setRentStepsToCreate,
    setRentStepsToUpdate,
  } = useRentSteps({
    lease,
    receivingAssistance,
    closeModal,
    refreshLDT,
    open,
  });

  function handleModalClose() {
    closeModal();
    resetRentSteps();
  }

  const rentStepHasError = rentSteps.some(
    (step) => !!step.errors?.amount || !!step.errors?.startDate,
  );

  const rentStepHasMissingRequiredFields = rentSteps.some((step) => {
    return _.isNil(step.amount) || step.amount === '' || !step.startDate;
  });

  // Simplified future rent step check - we only care about the last step
  const hasFutureRentStep =
    rentSteps.length > 0 &&
    moment(rentSteps[rentSteps.length - 1].startDate).isAfter(
      moment().startOf('day'),
    );
  // Check if any rent step has an invalid UUID as its id (meaning it's unsaved)
  const hasUnsavedRentStep =
    rentSteps.some(
      (step) => typeof step.id !== 'string' || !isValidUUID(step.id),
    ) && rentSteps.length > 1;

  const lastRentStepStartDate =
    rentSteps.length > 0 ? rentSteps[rentSteps.length - 1].startDate : null;

  const [affordableValuesByDate, setAffordableValuesByDate] = useState(null);
  useEffect(() => {
    if (lastRentStepStartDate) {
      const affVal = parseAffordableValues(
        affordableFloorplanPricing,
        null,
        false,
        unit,
        utilityAllowanceId,
        lastRentStepStartDate,
      );
      setAffordableValuesByDate({ ...affordableValues, ...affVal }); // override values with the ones taken by date
    } else setAffordableValuesByDate(affordableValues);
  }, [
    affordableFloorplanPricing,
    lastRentStepStartDate,
    unit,
    affordableValues,
    utilityAllowanceId,
  ]);
  const actions = (
    <>
      <Button
        variant="text"
        onClick={handleModalClose}
        disabled={rentStepsSaving}
      >
        Cancel
      </Button>
      <SubmitButton
        variant="primary"
        onClick={saveRentSteps}
        isSubmitting={rentStepsSaving}
        disabled={
          rentStepHasError ||
          rentStepHasMissingRequiredFields ||
          rentStepsSaving
        }
      >
        Save
      </SubmitButton>
    </>
  );

  // Simplified add rent step function - we only add if there's no future step
  function addRentStep() {
    if (hasFutureRentStep) return;

    setRentSteps((prev) => {
      const newRentStep = {
        id: prev.length,
        idx: prev.length,
        startDate: null,
        endDate: null,
      };
      setRentStepsToCreate((prev) => [...prev, newRentStep.id]);
      return [...prev, newRentStep];
    });
  }

  function onRentStepChange({ id, startDate, amount, name, idx }) {
    setRentSteps((prevRentSteps) => {
      const isExistingRentStep = typeof id === 'string';
      const currentStep = prevRentSteps.find((step) => step.idx === idx);
      const isLastStep = idx === prevRentSteps.length - 1;
      if (name === 'date') {
        const priorStep = prevRentSteps.find((step) => step.idx === idx - 1);
        // Simplified end date calculation since we know there's at most one future step
        const endDate = isLastStep
          ? null
          : formatDateDB(moment(startDate).clone().subtract(1, 'days'), false);

        const updatedCurrentStep = {
          ...currentStep,
          startDate,
          endDate,
          errors: getRentstepErrs(),
        };

        const updatedPriorStep = priorStep
          ? {
              ...priorStep,
              endDate: formatDateDB(
                moment(startDate).clone().subtract(1, 'days'),
                false,
              ),
            }
          : null;
        if (isExistingRentStep) {
          setRentStepsToUpdate((prev) => [...prev, updatedCurrentStep.id]);
        }
        if (updatedPriorStep) {
          setRentStepsToUpdate((prev) => [...prev, updatedPriorStep.id]);
        }

        return _.orderBy(
          [
            ...prevRentSteps.filter(
              (step) => step.id !== currentStep.id && step.id !== priorStep?.id,
            ),
            updatedCurrentStep,
            ...(updatedPriorStep ? [updatedPriorStep] : []),
          ],
          'idx',
          'asc',
        );
      } else {
        const updatedStep = {
          ...currentStep,
          amount,
          errors: getRentstepErrs(),
        };

        if (isExistingRentStep) {
          setRentStepsToUpdate((prev) => [...prev, updatedStep.id]);
        }

        return _.orderBy(
          [...prevRentSteps.filter((step) => step.id !== id), updatedStep],
          'idx',
          'asc',
        );
      }
    });

    function getRentstepErrs() {
      const errs = {
        amount: validate({
          leasedRent: amount,
          leaseRentPercentage,
          receivingAssistance: receivingAsstState,
          affordableValues: affordableValuesByDate,
          applicableRents,
          lease,
          unit,
          intl,
          flags,
          selectedProperty: property,
          hud236BasicRent,
          hud236MarketRent,
        }).leasedRent,
        startDate: moment(startDate).isBefore(
          moment(lease.startDate).endOf('day'),
        )
          ? 'Start date must be in the after move-in date'
          : null,
      };
      return errs;
    }
  }

  function onDeleteRentStep({ id, idx }) {
    if (idx === 0) return; // Prevent deleting first step

    const isExistingRentStep = typeof id === 'string';

    setRentSteps((prevRentSteps) => {
      const stepToDelete = prevRentSteps.find((step) => step.id === id);
      const priorStep = prevRentSteps.find((step) => step.idx === idx - 1);

      if (isExistingRentStep) {
        setRentStepsToDelete((prev) => [...prev, stepToDelete.id]);
        setRentStepsToUpdate((prev) =>
          prev.filter((stepId) => stepId !== stepToDelete.id),
        );
      }

      setRentStepsToCreate((prev) => prev.filter((stepId) => stepId !== id));

      if (isExistingRentStep) {
        setRentStepsToUpdate((prev) => [...prev, priorStep.id]);
      }

      return removeRentStep(prevRentSteps, { id, idx });
    });
  }

  return (
    <Modal
      open={open}
      onClose={handleModalClose}
      icon={<PencilIcon />}
      title={'Lease Rent Amount'}
      actions={actions}
      ModalProps={{
        PaperProps: {
          sx: {
            width: {
              xs: 'calc(100% - 32px)',
              sm: 600,
            },
          },
        },
      }}
    >
      <Grid container spacing={1} columnSpacing={3}>
        {/* Header */}
        <>
          <Grid item xs={4}>
            <Typography variant="labelLarge">Start Date</Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="labelLarge">End Date</Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="labelLarge">Lease Rent Amount</Typography>
          </Grid>
        </>
        {/* Rent Steps */}
        {rentSteps.map(({ id, startDate, endDate, amount, errors }, idx) => (
          <RentStep
            key={id}
            id={id}
            idx={idx}
            initialStartDate={startDate}
            initialEndDate={endDate}
            initialAmount={amount}
            onChange={onRentStepChange}
            onDelete={onDeleteRentStep}
            priorRentStep={rentSteps[idx - 1] ?? null}
            errors={errors}
            leaseEndDate={lease.endDate}
            leaseStartDate={lease.startDate}
            renewalLeaseStartDate={renewalLeaseStartDate}
            renewalOngoing={renewalIsRenewalComplete === false}
            saving={rentStepsSaving}
            tooltipMessage={intl.formatMessage(
              messages.rentstepModificationBannerText,
            )}
            canDelete={
              !isHUDorRD && idx !== 0 && moment(startDate).isAfter(moment())
            }
          />
        ))}
        {/* Add Rent Button */}
        <Grid item xs={12} paddingBottom={1}>
          <Tooltip
            title={
              isHUDorRD
                ? intl.formatMessage(messages.hudRdFloorPlanUpdateTooltip)
                : ''
            }
            placement="top"
            arrow
            disableHoverListener={!isHUDorRD}
          >
            <span>
              <Button
                variant="text"
                startIcon={<PlusIcon />}
                onClick={addRentStep}
                disabled={
                  rentStepHasError ||
                  rentStepHasMissingRequiredFields ||
                  rentStepsSaving ||
                  hasFutureRentStep ||
                  hasUnsavedRentStep ||
                  isHUDorRD
                }
              >
                Add Rent
              </Button>
            </span>
          </Tooltip>
        </Grid>
        {/* Housing Assistance */}
        {affordableValues.isAffordable && (
          <Grid item xs={12}>
            <InputLabel>Receives Housing Assistance</InputLabel>
            <Checkbox
              label="Yes"
              checked={receivingAsstState}
              onChange={(e) => {
                setReceivingAsstState(e.target.checked);
                receivingAssistanceUpdate(e.target.checked);
              }}
            />
          </Grid>
        )}
        {/* Banner */}
        <Grid item xs={12}>
          <Banner
            text={
              <Typography sx={{ textWrap: 'wrap' }} color={'text.secondary'}>
                {intl.formatMessage(messages.leaseRentBannerText)}
              </Typography>
            }
            color="lightGrey"
            icon={<AlertIcon />}
            hasBorder={false}
            BoxProps={{
              sx: { height: 'auto' },
            }}
          />
        </Grid>
      </Grid>
    </Modal>
  );
};

export default LeaseRentAmountModal;
